/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import { Select, Value } from "baseui/select";
import { ListItem, ListItemLabel } from "baseui/list";
import {
  FileTypeOption,
  getExtension,
  replaceExtension,
} from "../../Utils/Common";
import { TOP_ITEM_ZINDEX } from "../../Utils/SiteProps";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import DexterModal from "../../Shared/DexterModal";

export interface FileTypeModalItem {
  id: string;
  name: string;
  /** Current format code, e.g. "PROTO", "XML", "UNDETECTED" */
  format: string;
}

interface FileTypeModalProps {
  isOpen: boolean;
  /** The files the user selected */
  items: FileTypeModalItem[];
  /** Options whose `id` is the format code the backend uses */
  fileTypeOptions: FileTypeOption[];
  /** Pre-selected new format, e.g. from the "Apply to all" dropdown */
  initialFileType?: Value;
  onClose: () => void;
  /** Called with the new format code and the ids of files that change */
  onConfirm: (newFormat: string, changedIds: string[]) => void;
}

// Warning colours are translucent so they read on both light and dark themes.
const WARNING = {
  bg: "rgba(249, 115, 22, 0.12)",
  border: "rgba(249, 115, 22, 0.45)",
  text: "#F97316",
};

const RESULT_LIST_MIN_HEIGHT = "120px";

// Rough height of everything in the modal except the result list
// (DexterModal header + footer, warning, both fields, labels, summary).
// The list gets whatever is left of the screen, so the modal stays on screen.
const NON_LIST_HEIGHT = "560px";

const WarningIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill={WARNING.text}
    aria-hidden="true"
    style={{ flexShrink: 0, marginTop: "1px" }}
  >
    <path d="M12 2 1 21h22L12 2Zm1 15h-2v-2h2v2Zm0-4h-2V9h2v4Z" />
  </svg>
);

const FileTypeModal: React.FC<FileTypeModalProps> = ({
  isOpen,
  items,
  fileTypeOptions,
  initialFileType = [],
  onClose,
  onConfirm,
}) => {
  const { currentTheme } = useDexterTheme();
  const { colors, fonts, fontSizes } = currentTheme;

  const [newType, setNewType] = React.useState<Value>(initialFileType);

  // Re-apply the pre-selection each time the modal opens
  React.useEffect(() => {
    if (isOpen) setNewType(initialFileType);
  }, [isOpen, initialFileType]);

  const formatLabel = React.useCallback(
    (format: string): string => {
      if (format.toUpperCase() === "UNDETECTED") return "Unknown";
      return (
        fileTypeOptions.find(
          (option) => option.id.toUpperCase() === format.toUpperCase()
        )?.label ?? format
      );
    },
    [fileTypeOptions]
  );

  const files = items;

  const currentFormats = Array.from(
    new Set(files.map((file) => file.format.toUpperCase()))
  );

  // Short name for a format: its extension without the dot
  // (".proto" → "proto"), else the code in lowercase
  const formatShortName = (format: string): string => {
    if (format.toUpperCase() === "UNDETECTED") return "unknown";
    const extension = fileTypeOptions.find(
      (option) => option.id.toUpperCase() === format.toUpperCase()
    )?.extension;
    return extension ? extension.replace(/^\./, "") : format.toLowerCase();
  };

  const currentFormatLabel =
    currentFormats.length === 1
      ? formatLabel(currentFormats[0])
      : currentFormats.map(formatShortName).join(", ");

  const newFormat = (newType[0]?.id as string | undefined) ?? "";
  const newExtension =
    fileTypeOptions.find((option) => option.id === newFormat)?.extension ?? "";

  // A file changes if its format or its name's extension differs
  const changes = newFormat
    ? files
        .filter(
          (file) =>
            file.format.toUpperCase() !== newFormat.toUpperCase() ||
            getExtension(file.name) !== newExtension.toLowerCase()
        )
        .map((file) => ({
          ...file,
          newName: replaceExtension(file.name, newExtension),
        }))
    : [];
  const unchangedFileCount = newFormat ? files.length - changes.length : 0;

  const handleConfirm = (): void => {
    if (!newFormat || changes.length === 0) return;
    onConfirm(
      newFormat,
      changes.map((file) => file.id)
    );
    onClose();
  };

  // ---- Themed styles ----

  const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: "8px",
    fontFamily: fonts.primary,
    fontSize: fontSizes.medium,
    fontWeight: 600,
    color: colors.text,
  };

  const fieldBoxStyle: React.CSSProperties = {
    border: `1px solid ${colors.primaryLight}`,
    borderRadius: "8px",
    backgroundColor: colors.dropdownBackground,
    color: colors.text,
    padding: "12px 14px",
    fontFamily: fonts.primary,
    fontSize: fontSizes.medium,
  };

  const mutedTextStyle: React.CSSProperties = {
    color: colors.secondaryTextColor,
  };

  return (
    <DexterModal
      isOpen={isOpen}
      title="Change file type"
      confirmLabel="Confirm"
      confirmDisabled={changes.length === 0}
      onConfirm={handleConfirm}
      onCancel={onClose}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          color: colors.text,
          fontFamily: fonts.primary,
        }}
      >
        {/* Warning banner */}
        <div
          role="alert"
          style={{
            display: "flex",
            gap: "10px",
            padding: "12px 14px",
            marginBottom: "20px",
            borderRadius: "8px",
            backgroundColor: WARNING.bg,
            border: `1px solid ${WARNING.border}`,
            color: WARNING.text,
            fontSize: fontSizes.small,
            lineHeight: 1.45,
          }}
        >
          <WarningIcon />
          <span>
            Changing the file type could cause one or more files to become
            unstable and/or prevent opening of any associated mappings.
          </span>
        </div>

        {/* Current format (read-only) */}
        <div style={{ marginBottom: "20px" }}>
          <span style={labelStyle}>{`Current file type${
            currentFormats.length > 1 ? "s" : ""
          }`}</span>
          <div style={{ ...fieldBoxStyle, ...mutedTextStyle }}>
            {files.length ? currentFormatLabel : "—"}
          </div>
        </div>

        {/* New format */}
        <div style={{ marginBottom: "20px" }}>
          <span style={labelStyle}>New file type</span>
          <Select
            options={fileTypeOptions}
            value={newType}
            placeholder="Select format"
            searchable={false}
            clearable={false}
            onChange={({ value }) => setNewType(value)}
            overrides={{
              ControlContainer: {
                style: {
                  backgroundColor: colors.dropdownBackground,
                  borderTopWidth: "1px",
                  borderRightWidth: "1px",
                  borderBottomWidth: "1px",
                  borderLeftWidth: "1px",
                  borderTopColor: colors.primaryLight,
                  borderRightColor: colors.primaryLight,
                  borderBottomColor: colors.primaryLight,
                  borderLeftColor: colors.primaryLight,
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                  cursor: "pointer",
                },
              },
              SingleValue: {
                style: {
                  color: colors.text,
                  fontFamily: fonts.primary,
                  fontSize: fontSizes.medium,
                },
              },
              Placeholder: {
                style: {
                  color: colors.secondaryTextColor,
                  fontFamily: fonts.primary,
                  fontSize: fontSizes.medium,
                },
              },
              SelectArrow: {
                style: { color: colors.text },
              },
              Popover: {
                props: {
                  placement: "bottom",
                  popperOptions: {
                    modifiers: {
                      flip: { enabled: false },
                      preventOverflow: { enabled: false },
                    },
                  },
                  // DexterModal sits at TOP_ITEM_ZINDEX; keep the menu above it
                  overrides: {
                    Body: { style: { zIndex: TOP_ITEM_ZINDEX + 1 } },
                  },
                },
              },
              Dropdown: {
                style: {
                  backgroundColor: colors.dropdownBackground,
                  // Short enough to fit under the select inside the modal
                  maxHeight: "min(240px, 30vh)",
                },
              },
              DropdownListItem: {
                style: ({ $isHighlighted }: { $isHighlighted?: boolean }) => ({
                  backgroundColor: $isHighlighted
                    ? colors.selectionBackgroundPrimary
                    : colors.dropdownBackground,
                  color: colors.text,
                  fontFamily: fonts.primary,
                  fontSize: fontSizes.medium,
                }),
              },
            }}
          />
        </div>

        {/* Result preview */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={labelStyle}>Result</span>

          {newFormat && (
            <p
              style={{
                ...mutedTextStyle,
                margin: "0 0 8px",
                fontSize: fontSizes.small,
                lineHeight: 1.45,
              }}
            >
              {changes.length} {changes.length === 1 ? "file" : "files"} will
              change type and extension.
              {unchangedFileCount > 0 &&
                ` ${unchangedFileCount} ${
                  unchangedFileCount === 1
                    ? "file already has"
                    : "files already have"
                } this type.`}
            </p>
          )}

          <ul
            style={{
              ...fieldBoxStyle,
              boxSizing: "border-box",
              minHeight: RESULT_LIST_MIN_HEIGHT,
              // The list scrolls on its own so the modal fits the screen
              maxHeight: `max(${RESULT_LIST_MIN_HEIGHT}, calc(100dvh - ${NON_LIST_HEIGHT}))`,
              overflowY: "auto",
              margin: 0,
              // Rows run edge to edge; each ListItem pads itself
              padding: 0,
              listStyle: "none",
            }}
          >
            {!newFormat && (
              <li style={{ ...mutedTextStyle, padding: "12px 14px" }}>
                Choose a new file type to preview the changes.
              </li>
            )}
            {newFormat && changes.length === 0 && (
              <li style={{ ...mutedTextStyle, padding: "12px 14px" }}>
                All selected files already use {formatLabel(newFormat)}.
              </li>
            )}
            {changes.map((file, index) => {
              const isLast = index === changes.length - 1;
              return (
                <ListItem
                  key={file.id}
                  overrides={{
                    Root: {
                      style: {
                        backgroundColor: "transparent",
                        flexShrink: 0,
                        ":hover": {
                          backgroundColor: colors.dropdownItemHoverBackground,
                        },
                      },
                    },
                    Content: {
                      style: {
                        minHeight: "44px",
                        marginLeft: 0,
                        paddingLeft: "14px",
                        paddingRight: "14px",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        // Base Web draws its own divider here; replace it with the theme's
                        borderBottomWidth: isLast ? 0 : "1px",
                        borderBottomStyle: "solid",
                        borderBottomColor: colors.dropdownBorderColor,
                      },
                    },
                  }}
                >
                  <ListItemLabel
                    overrides={{
                      LabelContent: {
                        style: {
                          color: colors.tableTextColor,
                          fontFamily: fonts.primary,
                          fontSize: fontSizes.medium,
                          fontWeight: 600,
                          lineHeight: "20px",
                          overflowWrap: "anywhere",
                        },
                      },
                    }}
                  >
                    {file.name === file.newName
                      ? file.name
                      : `${file.name} → ${file.newName}`}
                  </ListItemLabel>
                </ListItem>
              );
            })}
          </ul>
        </div>
      </div>
    </DexterModal>
  );
};

export default FileTypeModal;
